import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Layers, ArrowLeft, Loader2, BookText, Play, Sparkles, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DIFFICULTY_TONE = {
  beginner:     'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  intermediate: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  advanced:     'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

export default function AdminFlashcards() {
  const [lists, setLists] = useState([]);
  const [meta, setMeta] = useState({ categories: [], preseed_languages: [] });
  const [filter, setFilter] = useState({ language_code: '', category: '', difficulty: '' });
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedJob, setSeedJob] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [metaRes, listsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/forge-vocab/categories`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/forge-vocab/lists`, {
          withCredentials: true,
          params: {
            language_code: filter.language_code || undefined,
            category:      filter.category      || undefined,
            difficulty:    filter.difficulty    || undefined,
          },
        }),
      ]);
      setMeta(metaRes.data || { categories: [], preseed_languages: [] });
      setLists(listsRes.data?.lists || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error('Admin access required.');
      } else {
        toast.error(`Failed to load flashcards: ${err?.response?.data?.detail || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSeedJob = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/forge-vocab/seed/status`, {
        withCredentials: true,
      });
      const job = res.data?.job || null;
      setSeedJob(job);
      if (!job || job.status === 'complete') {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setSeeding(false);
        if (job?.status === 'complete') {
          // Refresh the list grid one final time on completion.
          load();
        }
      }
    } catch (err) {
      console.warn('Seed status poll failed', err);
    }
  };

  const startSeed = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      await axios.post(`${BACKEND_URL}/api/forge-vocab/seed/start`, {}, {
        withCredentials: true,
      });
      toast.success('Seeding started in the background.');
      loadSeedJob();
      pollRef.current = setInterval(loadSeedJob, 4000);
    } catch (err) {
      setSeeding(false);
      toast.error(`Couldn't start seeding: ${err?.response?.data?.detail || err.message}`);
    }
  };

  useEffect(() => {
    load();
    loadSeedJob();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => { load(); }, [filter.language_code, filter.category, filter.difficulty]);

  const totalItems = lists.reduce((acc, l) => acc + (l.item_count || 0), 0);
  const jobRunning = seedJob && seedJob.status === 'running';
  const jobPct = seedJob && seedJob.total_pairs
    ? Math.round((seedJob.completed_pairs / seedJob.total_pairs) * 100)
    : 0;

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl" data-testid="admin-flashcards-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/admin/dashboard">
            <Button variant="ghost" size="sm" data-testid="back-to-admin">
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Layers className="h-6 w-6 text-cyan-400" />
              Flashcards
            </h1>
            <p className="text-sm text-muted-foreground">
              BYKI-style vocabulary lists. {lists.length} list{lists.length === 1 ? '' : 's'} ·{' '}
              {totalItems} item{totalItems === 1 ? '' : 's'}.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" size="sm" data-testid="reload-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Reload
          </Button>
          <Button
            onClick={startSeed}
            disabled={seeding || jobRunning}
            data-testid="seed-btn"
          >
            {seeding || jobRunning ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-1" />
            )}
            Seed all (Gemini)
          </Button>
        </div>
      </div>

      {/* Seed job status */}
      {seedJob && (
        <Card className="mb-5" data-testid="seed-status-card">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="font-medium">
                {jobRunning ? 'Seeding…' : 'Last seeding'}
                {' '}
                <span className="text-muted-foreground">
                  ({seedJob.languages?.join(', ')})
                </span>
              </span>
              <span className="text-muted-foreground text-xs">
                {seedJob.completed_pairs}/{seedJob.total_pairs} pairs
                {seedJob.failed_pairs ? ` · ${seedJob.failed_pairs} failed` : ''}
                {' · '}{seedJob.items_inserted || 0} added
                {seedJob.items_updated ? ` · ${seedJob.items_updated} updated` : ''}
              </span>
            </div>
            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-cyan-500 transition-all" style={{ width: `${jobPct}%` }} />
            </div>
            {seedJob.last_pair && jobRunning && (
              <div className="text-[11px] text-muted-foreground mt-1">last: {seedJob.last_pair}</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              className="bg-background border border-border rounded-md px-3 py-2 text-sm"
              value={filter.language_code}
              onChange={(e) => setFilter((f) => ({ ...f, language_code: e.target.value }))}
              data-testid="filter-language"
            >
              <option value="">All languages</option>
              {meta.preseed_languages.map((lc) => (
                <option key={lc} value={lc}>{lc.toUpperCase()}</option>
              ))}
            </select>
            <select
              className="bg-background border border-border rounded-md px-3 py-2 text-sm"
              value={filter.category}
              onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
              data-testid="filter-category"
            >
              <option value="">All categories</option>
              {meta.categories.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <select
              className="bg-background border border-border rounded-md px-3 py-2 text-sm"
              value={filter.difficulty}
              onChange={(e) => setFilter((f) => ({ ...f, difficulty: e.target.value }))}
              data-testid="filter-difficulty"
            >
              <option value="">All levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Lists */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : lists.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookText className="h-5 w-5 text-amber-400" />
              No flashcard lists yet
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              Click <span className="font-medium">Seed all (Gemini)</span> to generate{' '}
              {meta.preseed_languages.length || 6} languages × {meta.categories.length || 12}{' '}
              categories (~{(meta.preseed_languages.length || 6) * (meta.categories.length || 12) * 15} items).
            </p>
            <p>This runs in the background — feel free to navigate away.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="lists-grid">
          {lists.map((l) => (
            <Card key={l.list_id} className="hover:border-cyan-500/40 transition">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold truncate">{l.title}</h3>
                      <Badge variant="outline" className="text-[10px]">
                        {l.language_code.toUpperCase()}
                      </Badge>
                      {l.difficulty && (
                        <Badge variant="outline" className={`text-[10px] ${DIFFICULTY_TONE[l.difficulty] || ''}`}>
                          {l.difficulty}
                        </Badge>
                      )}
                      {l.is_system && (
                        <Badge variant="outline" className="text-[10px] bg-cyan-500/15 text-cyan-300 border-cyan-500/30">
                          system
                        </Badge>
                      )}
                    </div>
                    {l.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{l.description}</p>
                    )}
                    <div className="text-right text-xs text-muted-foreground mt-2">
                      {l.item_count || 0} item{l.item_count === 1 ? '' : 's'}
                    </div>
                  </div>
                  <Link to={`/admin/flashcards/study/${l.list_id}`}>
                    <Button
                      size="sm"
                      variant="default"
                      disabled={(l.item_count || 0) === 0}
                      data-testid={`study-btn-${l.list_id}`}
                    >
                      <Play className="h-3.5 w-3.5 mr-1" /> Study
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
